<script setup lang="ts">
import { reactive } from 'vue';
import type { StateObj } from '@vue-storefront/prestashop-api';
import type { AddressFormProps } from '~/components/AddressForm/types';

import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '../ui/button';

const props = defineProps<AddressFormProps>();
const emit = defineEmits(['on-address-list']);

const config = inject(Symbol.for('FormKitConfig')) as any;
const { locale, t } = useI18n();
config.locale = locale.value;

const { mutationAdd, mutationUpdate } = useAddresses();
const { data: psCountries, pending: countriesLoading, execute, groups } = useCountries();
const {
  result: vatResult,
  loading: vatLoading,
  error: vatError,
  validate: validateVat,
  validateDebounced: validateVatDebounced,
  reset: resetVat,
} = useVatValidation();

await execute();

const { savedAddress } = toRefs(props);

const refAddress = reactive({
  id_address: savedAddress?.value?.id ?? '',
  alias: savedAddress?.value?.alias ?? '',
  firstname: savedAddress?.value?.firstname ?? '',
  lastname: savedAddress?.value?.lastname ?? '',
  company: savedAddress?.value?.company ?? '',
  vat_number: savedAddress?.value?.vat_number ?? '',
  phone: savedAddress?.value?.phone ?? '',
  country: savedAddress?.value?.country ?? '',
  address1: savedAddress?.value?.address1 ?? '',
  address2: savedAddress?.value?.address2 ?? '',
  city: savedAddress?.value?.city ?? '',
  state: savedAddress?.value?.state ?? '',
  postcode: savedAddress?.value?.postcode ?? '',
  id_country: savedAddress?.value?.id_country ?? '',
  id_state: savedAddress?.value?.id_state ?? '',
  id: savedAddress?.value?.id ?? '',
  state_iso: savedAddress?.value?.state_iso ?? '',
  dni: savedAddress?.value?.dni ?? '',
  country_iso: savedAddress?.value?.country_iso ?? '',
  other: savedAddress?.value?.other ?? '',
  phone_mobile: savedAddress?.value?.phone_mobile ?? '',
  fatturazione:  {...savedAddress?.value?.fatturazione},
});

const invoiceNeeded = ref(
  savedAddress?.value?.fatturazione && Object.values(savedAddress.value.fatturazione).some((value) => value),
);

const errors = ref<any>([]);
const selectedCountry = ref(refAddress.id_country);
const selectedState = ref(refAddress.id_state);

const countriesList = computed(() =>
  psCountries.value?.map((country) => ({
    value: country.id_country,
    label: country.name,
  })),
);

const statesList = computed(() => {
  const country = psCountries.value?.find((el) => el.id_country === selectedCountry.value);
  return (
    country?.states?.map((state: StateObj) => ({
      value: state.id_state,
      label: state.name,
    })) || []
  );
});

const isCountryLoading = computed(() => countriesLoading.value);
const isStatesRequired = computed(() => statesList.value.length > 0);
const selectedCountryData = computed(() =>
  psCountries.value?.find((el) => String(el.id_country) === String(refAddress.id_country ?? selectedCountry.value)),
);
const countryIso = computed(() =>
  String(selectedCountryData.value?.iso_code || refAddress.country_iso || '').toUpperCase(),
);
const euCountryCodes = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR',
  'GR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO',
  'SE', 'SI', 'SK',
]);
const isEuCountry = computed(() => {
  const isEu = selectedCountryData.value?.is_eu;
  return isEu === true || ['true', '1'].includes(String(isEu)) || euCountryCodes.has(countryIso.value);
});

// Initialize fatturazione fields for reactivity when groups data is available
watch(
  [() => groups.value?.fatturazione?.fields, () => refAddress.id_country],
  ([fields, countryId]) => {
    if (fields && countryId == 10) {
      fields.forEach((field: any) => {
        if (!(field.slug in refAddress.fatturazione)) {
          refAddress.fatturazione[field.slug] = savedAddress?.value?.fatturazione?.[field.slug] ?? '';
        }
      });
    }
  },
  { immediate: true }
);

watch(
  () => refAddress.id_country,
  (newVal) => {
    selectedCountry.value = newVal;
    const states = psCountries.value?.find((el) => el.id_country === newVal)?.states || [];
    if (!states.length) {
      selectedState.value = '';
      refAddress.id_state = '';
      refAddress.state = '';
    }
    resetVat();
  },
);

watch(
  () => selectedState.value,
  (newVal) => {
    refAddress.id_state = newVal || '';
  },
);

const backToAllAddresses = () => emit('on-address-list');

const onSubmit = async () => {
  errors.value = [];

  // Remove fatturazione if id_country is not 10
  if (refAddress.id_country === 10 && invoiceNeeded.value === true) {

  } else {
    // Remove fatturazione if id_country is not 10
    delete refAddress.fatturazione;
  }

  const action = refAddress.id_address ? mutationUpdate.mutate : mutationAdd.mutate;

  action(
    { address: refAddress },
    {
      onError: (error) => {
        errors.value = error?.message;
      },
      onSuccess: (data, error) => {
        if (data.success) {
          backToAllAddresses();
        } else {
          errors.value = (data as any).errors ?? data.message;
        }
      },
    },
  );
};

const formatText = (text: string): string =>
  text
    .split('_')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Disable save when a VAT number is entered but has not passed validation (EU only)
const isSubmitDisabled = computed(() => {
  if (!isEuCountry.value) return false;
  const vat = refAddress.vat_number?.trim();
  if (!vat) return false;
  if (vatLoading.value) return true;
  if (vatError.value) return true;
  return vatResult.value?.valid !== true;
});

function onVatInput() {
  nextTick(() => {
    if (!isEuCountry.value) {
      resetVat();
      return;
    }

    validateVatDebounced(refAddress.vat_number ?? '', countryIso.value || undefined);
  });
}

async function onVatBlur() {
  if (!isEuCountry.value) return;
  const vat = refAddress.vat_number?.trim();
  if (!vat) return;
  await validateVat(vat, countryIso.value || undefined);
  if (vatResult.value?.valid) {
    if (vatResult.value.name && !refAddress.company) {
      refAddress.company = vatResult.value.name;
    }
    if (vatResult.value.parsed_city && !refAddress.city) {
      refAddress.city = vatResult.value.parsed_city;
    }
    if (vatResult.value.parsed_zip && !refAddress.postcode) {
      refAddress.postcode = vatResult.value.parsed_zip;
    }
  }
}
</script>

<template>
  <FormKit type="form" @submit="onSubmit" :actions="false" :errors="errors">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
      <div class="col-span-full form-wrapper">
        <FormKit
          name="alias"
          v-model="refAddress.alias"          :label="t('Alias')"
          :help="t('Alias field message')"
          type="text"
          validation="required"
        />
      </div>
      <div class="col-span-full md:col-span-1 form-wrapper">
        <FormKit name="firstName" v-model="refAddress.firstname" :label="t('First name')" type="text" validation="required" />
      </div>
      <div class="col-span-full md:col-span-1 form-wrapper">        <FormKit name="lastName" v-model="refAddress.lastname" :label="t('Last name')" type="text" validation="required" />
      </div>
      <div class="col-span-full md:col-span-1 form-wrapper">
        <FormKit name="company" v-model="refAddress.company" :label="t('Company')" type="text" />
      </div>
      <div class="col-span-full md:col-span-1 form-wrapper">
        <FormKit name="phone" v-model="refAddress.phone" :label="t('Phone number')" type="text" validation="required" />
      </div>
      <div class="col-span-full md:col-span-1 form-wrapper">
        <FormKit
          type="select"          :label="t('Select your country')"
          v-model="refAddress.id_country"
          :disabled="isCountryLoading"
          name="country"
          :options="[{ label: t('Select your country') + '...', value: null }, ...countriesList]"
          validation="required"
        />
      </div>
      <div v-if="isStatesRequired" class="col-span-full md:col-span-1 form-wrapper">
        <FormKit
          type="select"          :label="t('Select your state')"
          v-model="refAddress.id_state"
          :disabled="isCountryLoading || !statesList.length"
          name="state"
          :options="[{ label: t('Select your state') + '...', value: null }, ...statesList]"
          :validation="isStatesRequired ? 'required' : ''"
        />
      </div>
      <div class="col-span-full md:col-span-1 form-wrapper">        <FormKit name="city" v-model="refAddress.city" :label="t('City')" type="text" validation="required" />
      </div>
      <div class="col-span-full md:col-span-1 form-wrapper">
        <FormKit name="streetName" v-model="refAddress.address1" :label="t('Address')" type="text" validation="required" />
      </div>
      <div class="col-span-full md:col-span-1 form-wrapper">
        <FormKit name="streetName2" v-model="refAddress.address2" :label="t('Address2')" type="text" />
      </div>
      <div class="col-span-full md:col-span-1 form-wrapper">
        <FormKit name="postalCode" v-model="refAddress.postcode" :label="t('Zip-code')" type="text" validation="required" />
      </div>

      <template v-if="refAddress.id_country != 10">
        <div class="col-span-full form-wrapper">
          <FormKit
            name="vat"
            v-model="refAddress.vat_number"
            :label="t('VAT')"
            :help="t('VAT message info')"
            type="text"
            @input="onVatInput"
            @blur="onVatBlur"
          />
          <template v-if="isEuCountry">
            <p v-if="vatLoading" class="mt-1 text-sm text-blue-600">{{ t('Checking VAT number…') }}</p>
            <p v-else-if="vatError" class="mt-1 text-sm text-red-500">{{ vatError }}</p>
            <p v-else-if="vatResult?.valid === false" class="mt-1 text-sm text-red-500">{{ t('VAT number is not valid') }}</p>
            <p v-else-if="vatResult?.valid" class="mt-1 text-sm text-green-600">{{ t('VAT number is valid') }}</p>
          </template>
        </div>
      </template>

      <template v-if="refAddress.id_country == 10">
        <div class="col-span-full my-3 flex items-center space-x-3">
          <Switch id="invoiceActivation" v-model="invoiceNeeded" />
          <Label for="invoiceActivation" class="italic font-semibold">Sì, voglio la fattura.</Label>
        </div>

        <template v-if="groups?.fatturazione?.fields && invoiceNeeded">
          <div
            v-for="(field, index) in groups?.fatturazione?.fields"
            :key="field['slug'] + index"
            class="col-span-full md:col-span-1 form-wrapper"
          >
            <FormKit
              :name="field['slug']"
              v-model="refAddress.fatturazione[field['slug']]"
              :label="t(field['slug'], formatText(field['slug']))"
              :type="field['type']"
              :required="field.require"
            />
          </div>
        </template>
      </template>

      <FormKitMessages />
      <div class="md:col-span-2 flex mt-6 gap-4">
        <Button type="submit" :disabled="isSubmitDisabled">
          {{ t('Save address') }}
        </Button>
        <Button type="reset" class="min-w-40" variant="secondary" @click.prevent="backToAllAddresses">
          {{ t('Cancel') }}
        </Button>
      </div>
    </div>
  </FormKit>
</template>
